import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "mj_cart_v1";

function keyFor(pid, variant) { return variant ? `${pid}::${variant}` : pid; }

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  // Lazy initializer: reads localStorage synchronously on first render —
  // no effect-ordering race with the save effect.
  const [items, setItems] = useState(loadFromStorage);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, variant) => {
    const k = keyFor(product.id, variant?.label);
    const price = product.price + (variant?.delta || 0);
    setItems((cur) => {
      const idx = cur.findIndex((i) => i.key === k);
      if (idx >= 0) {
        const copy = [...cur];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...cur, {
        key: k,
        product_id: product.id,
        name: product.name,
        price,
        qty: 1,
        variant: variant?.label || null,
        image: product.image,
        note: "",
      }];
    });
    setCartOpen(true);
  };

  const setQty = (key, qty) =>
    setItems((cur) => qty <= 0 ? cur.filter((i) => i.key !== key) : cur.map((i) => (i.key === key ? { ...i, qty } : i)));

  const setNote = (key, note) =>
    setItems((cur) => cur.map((i) => (i.key === key ? { ...i, note } : i)));

  const remove = (key) => setItems((cur) => cur.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    // Delivery fee is determined at checkout based on distance — not counted here.
    return { subtotal, delivery_fee: 0, tax: 0, total: subtotal, count: items.reduce((s, i) => s + i.qty, 0) };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, setNote, remove, clear, cartOpen, setCartOpen, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
